import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isFeatureEnabled } from '../constants/featureFlags';
import { resolvePrimaryRole } from '../utils/roleRouting';
import { OnboardingContext, DISABLED_ONBOARDING } from './OnboardingContext';
import {
  TOUR_VERSION,
  SCREEN_DASHBOARD,
  MIN_TARGETED_STEPS,
  START_DELAY_MS,
  TARGET_SETTLE_TIMEOUT_MS,
  BODY_ACTIVE_CLASS,
  NAV_GROUP_LETTER_SOUND,
  NAV_GROUP_MANAGEMENT,
  NAV_GROUP_HELP,
  PHONE_MAX_WIDTH,
  TEACHER_DASHBOARD_PATH,
} from './constants';
import {
  buildTourSteps,
  filterStepsToPresentTargets,
  countTargetedSteps,
  findFirstAssessmentVideo,
  targetSelector,
  isTargetPresent,
  HINTS,
  HINT_DISMISS_LABEL,
  TOUR_SKIP_LABEL,
  TOUR_BACK_LABEL,
  TOUR_NEXT_LABEL,
} from './steps';
import { evaluateAutoStart, hasSeenHint, isProfileLoaded } from './eligibility';
import { readOnboardingState, recordTourStarted, recordTourResult, recordHintSeen } from './persistence';
import { loadTourEngine, baseDriverConfig } from './tourEngine';
import { waitForTargetSettled } from './domReady';
import { isAppModalOpen } from './modalGuard';
import { installAssessmentKeySwallow } from './keySwallow';
import { firstNameFrom } from './firstName';
import { formatDuration } from './helpVideos';
import { useHelpVideos } from './useHelpVideos';
import HelpVideosPanel from './HelpVideosPanel';
import HelpVideoPlayer from './HelpVideoPlayer';
import './onboarding.css';

const isPhoneWidth = () => typeof window !== 'undefined' && window.innerWidth <= PHONE_MAX_WIDTH;

export function watchLabelFor(video) {
  const d = formatDuration(video && video.durationSec);
  return `Watch: ${video.title}${d ? ` · ${d}` : ''}`;
}

/**
 * useOnboardingTour — owns the single driver.js instance for the welcome
 * tour and the one-at-a-time contextual hints, the readiness reports from
 * pages, the nav choreography, seen-state persistence and (through
 * useHelpVideos) the How-to videos panel/player. Mounted once per
 * authenticated session by OnboardingProvider (inside the Router, so route
 * changes and replay navigation are visible). `videosEnabled` mirrors the
 * helpVideos flag: false makes the videos side inert (no read, no Watch
 * button, no panel) while the tour and hints run exactly as before.
 */
export function useOnboardingTour({ user, isNavOpen, setIsNavOpen, appModalOpen = false, systemModalOpen = false, videosEnabled = true }) {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;

  const [screens, setScreens] = useState({});
  const [tourActive, setTourActive] = useState(false);
  const [hintActive, setHintActive] = useState(false);
  const [onboardingState, setOnboardingState] = useState(() => readOnboardingState(user));

  const driverRef = useRef(null);
  const hintDriverRef = useRef(null);
  const hintEndRef = useRef(null);
  const startingRef = useRef(false);
  const stepsRef = useRef([]);
  const stepIndexRef = useRef(0);
  const cleanupFnsRef = useRef([]);
  const preTourNavRef = useRef(null);
  const focusReturnRef = useRef(null);
  const replayPendingRef = useRef(false);
  const navControlsRef = useRef(null);
  const hintsShownRef = useRef(new Set());
  const userRef = useRef(user);
  const isNavOpenRef = useRef(isNavOpen);
  const pathnameRef = useRef(pathname);
  const modalRef = useRef(false);
  // React Router's useNavigate re-creates its function on every location
  // change; keep it (and startTour) behind refs so effect timers are never
  // cleared by an identity change mid-countdown.
  const navigateRef = useRef(navigate);
  const startTourRef = useRef(null);
  userRef.current = user;
  isNavOpenRef.current = isNavOpen;
  pathnameRef.current = pathname;
  modalRef.current = !!(appModalOpen || systemModalOpen);
  navigateRef.current = navigate;

  // ── How-to videos: one doc read, on demand, cached per session ────────
  const {
    videos, videosRef, panelOpen, activeVideo,
    prefetchHelpVideos, openHelpVideos, closeHelpVideos, playVideo, closePlayer,
    panelReturnRef, playerReturnRef,
  } = useHelpVideos({ userRef, enabled: videosEnabled });

  useEffect(() => {
    setOnboardingState(readOnboardingState(user));
  }, [user]);

  // ── pages report readiness ─────────────────────────────────────────────
  const reportScreen = useCallback((key, payload) => {
    const ready = !!(payload && payload.ready);
    const classCount = payload && Number.isFinite(payload.classCount) ? payload.classCount : 0;
    setScreens((prev) => {
      const cur = prev[key];
      if (cur && cur.ready === ready && cur.classCount === classCount) return prev;
      return { ...prev, [key]: { ready, classCount } };
    });
  }, []);

  const registerNavControls = useCallback((controls) => {
    navControlsRef.current = controls;
    return () => {
      if (navControlsRef.current === controls) navControlsRef.current = null;
    };
  }, []);

  // ── nav choreography ───────────────────────────────────────────────────
  const applyNavState = useCallback((navState) => {
    if (!navState) return;
    const controls = navControlsRef.current;
    if (navState.drawer === 'open') {
      if (!isNavOpenRef.current && setIsNavOpen) setIsNavOpen(true);
      if (navState.expandGroup && controls && controls.setGroupCollapsed) controls.setGroupCollapsed(navState.expandGroup, false);
    } else if (navState.drawer === 'closed' && isPhoneWidth() && isNavOpenRef.current && setIsNavOpen) {
      setIsNavOpen(false);
    }
  }, [setIsNavOpen]);

  const snapshotNav = useCallback(() => {
    const controls = navControlsRef.current;
    const collapsed = controls && controls.getCollapsedGroups ? controls.getCollapsedGroups() : null;
    preTourNavRef.current = { navOpen: !!isNavOpenRef.current, collapsed };
  }, []);

  const restoreNav = useCallback(() => {
    const saved = preTourNavRef.current;
    preTourNavRef.current = null;
    if (!saved) return;
    const controls = navControlsRef.current;
    if (setIsNavOpen && saved.navOpen !== isNavOpenRef.current) setIsNavOpen(saved.navOpen);
    if (controls && controls.setGroupCollapsed && saved.collapsed) {
      [NAV_GROUP_LETTER_SOUND, NAV_GROUP_MANAGEMENT, NAV_GROUP_HELP].forEach((g) => {
        if (g in saved.collapsed) controls.setGroupCollapsed(g, !!saved.collapsed[g]);
      });
    }
  }, [setIsNavOpen]);

  const restoreFocus = useCallback(() => {
    const el = focusReturnRef.current;
    focusReturnRef.current = null;
    if (el && typeof document !== 'undefined' && document.contains(el) && typeof el.focus === 'function') {
      try { el.focus(); } catch (e) { /* non-fatal */ }
    }
  }, []);

  const teardownTourChrome = useCallback(() => {
    cleanupFnsRef.current.forEach((fn) => { try { fn(); } catch (e) { /* non-fatal */ } });
    cleanupFnsRef.current = [];
    if (typeof document !== 'undefined') document.body.classList.remove(BODY_ACTIVE_CLASS);
    restoreNav();
  }, [restoreNav]);

  /** End the tour. 'completed' | 'skipped' record state; 'aborted' (sign-out, unmount) writes nothing. */
  const endTour = useCallback((outcome) => {
    const d = driverRef.current;
    if (!d) return;
    driverRef.current = null;
    const lastStep = stepIndexRef.current;
    try { d.destroy(); } catch (e) { /* non-fatal */ }
    teardownTourChrome();
    setTourActive(false);
    if (outcome === 'completed' || outcome === 'skipped') {
      setOnboardingState(recordTourResult(userRef.current, { outcome, lastStep }));
    }
    restoreFocus();
  }, [teardownTourChrome, restoreFocus]);

  const endTourRef = useRef(endTour);
  endTourRef.current = endTour;
  const playVideoRef = useRef(playVideo);
  playVideoRef.current = playVideo;

  const goToStep = useCallback(async (idx) => {
    const d = driverRef.current;
    const step = stepsRef.current[idx];
    if (!d || !step) return;
    applyNavState(step.navState);
    await waitForTargetSettled(step.target ? targetSelector(step.target) : null, TARGET_SETTLE_TIMEOUT_MS);
    if (driverRef.current !== d) return;
    stepIndexRef.current = idx;
    d.moveTo(idx);
  }, [applyNavState]);

  const decoratePopover = useCallback((popover, step, idx, { isTour }) => {
    if (!popover || !popover.wrapper) return;
    const wrapper = popover.wrapper;
    wrapper.setAttribute('role', 'dialog');
    wrapper.setAttribute('aria-modal', 'true');
    wrapper.setAttribute('data-onboarding', isTour ? 'tour' : 'hint');
    wrapper.setAttribute('data-onboarding-step', step.id || step.key || String(idx));
    if (popover.title) {
      const titleId = `onboarding-popover-title-${step.id || step.key || idx}`;
      popover.title.id = titleId;
      wrapper.setAttribute('aria-labelledby', titleId);
    }
    if (isTour && popover.footer) {
      const skip = document.createElement('button');
      skip.type = 'button';
      skip.className = 'driver-popover-footer-btn onboarding-skip-btn';
      skip.textContent = TOUR_SKIP_LABEL;
      skip.setAttribute('aria-label', 'Skip the tour');
      skip.addEventListener('click', () => endTourRef.current('skipped'));
      popover.footer.insertBefore(skip, popover.footer.firstChild);
    }
    if (isTour && step.id === 'ready') {
      const video = findFirstAssessmentVideo(videosRef.current.list);
      if (video) {
        const watch = document.createElement('button');
        watch.type = 'button';
        watch.className = 'driver-popover-footer-btn onboarding-secondary-btn';
        watch.textContent = watchLabelFor(video);
        watch.addEventListener('click', () => {
          endTourRef.current('completed');
          playVideoRef.current(video, null);
        });
        wrapper.appendChild(watch);
      }
    }
    const primary = popover.nextButton;
    if (primary && typeof primary.focus === 'function') {
      window.setTimeout(() => { try { primary.focus(); } catch (e) { /* non-fatal */ } }, 0);
    }
  }, [videosRef]); // stable ref from useHelpVideos — listed only for exhaustive-deps

  const toDriverSteps = useCallback((steps) => {
    const last = steps.length - 1;
    return steps.map((step, idx) => ({
      element: step.target ? targetSelector(step.target) : undefined,
      popover: {
        title: step.title,
        description: step.body,
        side: step.side || 'bottom',
        align: 'start',
        showButtons: idx === 0 ? ['next'] : ['next', 'previous'],
        nextBtnText: step.nextLabel || (idx === last ? 'Done' : TOUR_NEXT_LABEL),
        prevBtnText: TOUR_BACK_LABEL,
        doneBtnText: step.nextLabel || 'Done',
        showProgress: !!step.showProgress,
        progressText: '{{current}} of {{total}}',
        onNextClick: () => { if (idx === last) endTourRef.current('completed'); else goToStep(idx + 1); },
        onPrevClick: () => { if (idx > 0) goToStep(idx - 1); },
        onPopoverRender: (popover) => decoratePopover(popover, step, idx, { isTour: true }),
      },
    }));
  }, [goToStep, decoratePopover]);

  const endHintRef = useRef(null);
  const endHint = useCallback(() => {
    if (hintEndRef.current) hintEndRef.current();
  }, []);
  endHintRef.current = endHint;

  /**
   * Start the tour. reason 'auto' (eligibility already met — re-checked
   * here for modals + targets) or 'replay' (Help → Take the tour: any role,
   * any route; navigates to the dashboard first when needed).
   */
  const startTour = useCallback(async ({ reason = 'replay', returnFocusTo = null } = {}) => {
    if (driverRef.current || startingRef.current) return false;
    if (reason === 'replay' && pathnameRef.current !== TEACHER_DASHBOARD_PATH) {
      replayPendingRef.current = true;
      focusReturnRef.current = returnFocusTo;
      navigateRef.current(TEACHER_DASHBOARD_PATH);
      return true;
    }
    startingRef.current = true;
    try {
      const u = userRef.current;
      if (!u) return false;
      focusReturnRef.current = returnFocusTo || focusReturnRef.current || (typeof document !== 'undefined' ? document.activeElement : null);
      endHintRef.current();
      prefetchHelpVideos();
      const primary = resolvePrimaryRole(u.role);
      const firstName = firstNameFrom((u.profile && u.profile.displayName) || u.displayName);
      const steps = filterStepsToPresentTargets(buildTourSteps(primary, { firstName }));
      if (steps.length === 0) return false;
      if (reason === 'auto') {
        if (countTargetedSteps(steps) < MIN_TARGETED_STEPS) return false;
        if (modalRef.current || isAppModalOpen()) return false;
      }
      const { driver } = await loadTourEngine();
      if (driverRef.current || !userRef.current || pathnameRef.current !== TEACHER_DASHBOARD_PATH) return false;
      if (reason === 'auto' && (modalRef.current || isAppModalOpen())) return false;
      const present = filterStepsToPresentTargets(steps);
      if (present.length === 0 || (reason === 'auto' && countTargetedSteps(present) < MIN_TARGETED_STEPS)) return false;

      snapshotNav();
      if (typeof document !== 'undefined') document.body.classList.add(BODY_ACTIVE_CLASS);
      stepsRef.current = present;
      stepIndexRef.current = 0;
      const d = driver({
        ...baseDriverConfig(),
        steps: toDriverSteps(present),
        onDestroyed: () => {
          // driver ended itself (never expected — allowClose is off); treat as skip
          if (driverRef.current === d) {
            driverRef.current = null;
            teardownTourChrome();
            setTourActive(false);
            setOnboardingState(recordTourResult(userRef.current, { outcome: 'skipped', lastStep: stepIndexRef.current }));
            restoreFocus();
          }
        },
      });
      driverRef.current = d;
      const onKey = (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          endTourRef.current('skipped');
        }
      };
      window.addEventListener('keydown', onKey, true);
      cleanupFnsRef.current.push(() => window.removeEventListener('keydown', onKey, true));
      setTourActive(true);
      applyNavState(present[0].navState);
      await waitForTargetSettled(present[0].target ? targetSelector(present[0].target) : null, TARGET_SETTLE_TIMEOUT_MS);
      if (driverRef.current !== d) return false;
      // Once-only guarantee: an AUTO-start counts as seen the moment the
      // first step is about to render — tourVersion + tourStartedAt land now
      // (best-effort), so a closed tab / lost network / sign-out mid-tour
      // never re-shows it on the next login or another device. Replay
      // (Help → Take the tour) writes nothing here.
      if (reason === 'auto') setOnboardingState(recordTourStarted(userRef.current));
      d.drive(0);
      return true;
    } finally {
      startingRef.current = false;
    }
  }, [prefetchHelpVideos, snapshotNav, toDriverSteps, teardownTourChrome, restoreFocus, applyNavState]);
  startTourRef.current = startTour;

  // ── contextual hints (one at a time, once per user, never during the tour) ──
  const showHint = useCallback(async (key) => {
    const def = HINTS[key];
    if (!def || hintDriverRef.current || driverRef.current || startingRef.current) return false;
    if (modalRef.current || isAppModalOpen()) return false;
    hintsShownRef.current.add(key);
    const { driver } = await loadTourEngine();
    if (hintDriverRef.current || driverRef.current || !userRef.current || !isTargetPresent(def.target)) return false;
    setOnboardingState(recordHintSeen(userRef.current, key));
    const removers = [];
    const focusBefore = typeof document !== 'undefined' ? document.activeElement : null;
    let d = null;
    const end = () => {
      if (hintDriverRef.current !== d) return;
      hintDriverRef.current = null;
      hintEndRef.current = null;
      try { d.destroy(); } catch (e) { /* non-fatal */ }
      removers.forEach((fn) => { try { fn(); } catch (e) { /* non-fatal */ } });
      setHintActive(false);
      if (focusBefore && document.contains(focusBefore) && typeof focusBefore.focus === 'function') {
        try { focusBefore.focus(); } catch (e) { /* non-fatal */ }
      }
    };
    if (def.swallowAssessmentKeys) removers.push(installAssessmentKeySwallow(window));
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        end();
      }
    };
    window.addEventListener('keydown', onKey, true);
    removers.push(() => window.removeEventListener('keydown', onKey, true));
    d = driver({
      ...baseDriverConfig(),
      steps: [{
        element: targetSelector(def.target),
        popover: {
          title: def.title,
          description: def.body,
          side: def.side || 'bottom',
          align: 'start',
          showButtons: ['next'],
          nextBtnText: HINT_DISMISS_LABEL,
          doneBtnText: HINT_DISMISS_LABEL,
          showProgress: false,
          onNextClick: () => end(),
          onPopoverRender: (popover) => decoratePopover(popover, def, 0, { isTour: false }),
        },
      }],
      onDestroyed: () => {
        if (hintDriverRef.current === d) {
          hintDriverRef.current = null;
          hintEndRef.current = null;
          removers.forEach((fn) => { try { fn(); } catch (e) { /* non-fatal */ } });
          setHintActive(false);
        }
      },
    });
    hintDriverRef.current = d;
    hintEndRef.current = end;
    setHintActive(true);
    d.drive(0);
    return true;
  }, [decoratePopover]);

  // ── auto-start (dashboard readiness → one frame + START_DELAY_MS) ─────
  const dash = screens[SCREEN_DASHBOARD] || { ready: false, classCount: 0 };
  useEffect(() => {
    const verdict = evaluateAutoStart({
      flagOn: true,
      profileLoaded: isProfileLoaded(user),
      primaryRole: resolvePrimaryRole(user && user.role),
      classCount: dash.classCount,
      pathname,
      dashboardReady: dash.ready,
      onboarding: onboardingState,
      modalOpen: !!(appModalOpen || systemModalOpen),
      tourActive: tourActive || hintActive,
    });
    if (!verdict.eligible) return undefined;
    let cancelled = false;
    let timer = null;
    const raf = window.requestAnimationFrame(() => {
      timer = window.setTimeout(() => {
        if (!cancelled) startTourRef.current({ reason: 'auto' });
      }, START_DELAY_MS);
    });
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(raf);
      if (timer) window.clearTimeout(timer);
    };
  }, [dash.ready, dash.classCount, pathname, user, onboardingState, appModalOpen, systemModalOpen, tourActive, hintActive]);

  // ── replay arrival: Help → Take the tour from another route ───────────
  useEffect(() => {
    if (!replayPendingRef.current || pathname !== TEACHER_DASHBOARD_PATH || !dash.ready) return undefined;
    // The pending flag clears only when the timer fires, so a re-run of this
    // effect (another render before the delay elapses) re-arms instead of
    // losing the replay.
    const timer = window.setTimeout(() => {
      replayPendingRef.current = false;
      startTourRef.current({ reason: 'replay', returnFocusTo: focusReturnRef.current });
    }, START_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [pathname, dash.ready]);

  // ── hints: first mount with the target present ─────────────────────────
  useEffect(() => {
    if (tourActive || hintActive || appModalOpen || systemModalOpen) return;
    // Same ordering guarantee as the tour: no profile object → the seen-state
    // is unknown, never "unseen" — no hint until the profile is loaded.
    if (!isProfileLoaded(user)) return;
    for (const key of Object.keys(HINTS)) {
      const s = screens[key];
      if (!s || !s.ready) continue;
      if (hintsShownRef.current.has(key) || hasSeenHint(onboardingState, key)) continue;
      if (!isTargetPresent(HINTS[key].target)) continue;
      showHint(key);
      break;
    }
  }, [screens, tourActive, hintActive, onboardingState, appModalOpen, systemModalOpen, showHint, user]);

  // ── route change: end a running tour (records the step) / drop a hint ──
  useEffect(() => {
    if (driverRef.current && pathname !== TEACHER_DASHBOARD_PATH) endTour('skipped');
    if (hintDriverRef.current) endHint();
  }, [pathname, endTour, endHint]);

  // ── idle warning / MFA mid-tour: end cleanly with Skip semantics ───────
  useEffect(() => {
    if (!systemModalOpen) return;
    if (driverRef.current) endTour('skipped');
    if (hintDriverRef.current) endHint();
  }, [systemModalOpen, endTour, endHint]);

  // ── sign-out / user switch / unmount: destroy, never write ─────────────
  const hardStop = useCallback(() => {
    const d = driverRef.current;
    if (d) {
      driverRef.current = null;
      try { d.destroy(); } catch (e) { /* non-fatal */ }
      teardownTourChrome();
      setTourActive(false);
    }
    if (hintDriverRef.current) endHint();
    replayPendingRef.current = false;
  }, [teardownTourChrome, endHint]);
  const hardStopRef = useRef(hardStop);
  hardStopRef.current = hardStop;
  const uid = user && user.uid;
  useEffect(() => () => hardStopRef.current(), [uid]);

  const value = useMemo(() => ({
    enabled: true,
    videosEnabled: !!videosEnabled,
    tourActive,
    reportScreen,
    registerNavControls,
    startTour: (opts) => startTour({ reason: 'replay', ...(opts || {}) }),
    prefetchHelpVideos,
    openHelpVideos,
    helpVideosStatus: videos.status,
    hasHelpVideos: videos.list.length > 0,
    tourVersion: TOUR_VERSION,
  }), [videosEnabled, tourActive, reportScreen, registerNavControls, startTour, prefetchHelpVideos, openHelpVideos, videos]);

  return {
    value,
    panelOpen,
    videos,
    activeVideo,
    closeHelpVideos,
    playVideo,
    closePlayer,
    panelReturnRef,
    playerReturnRef,
  };
}

/** The How-to videos panel + click-to-play player, mounted only when the helpVideos flag is on. */
function HelpVideosSurface({ hv }) {
  return (
    <>
      {hv.panelOpen && (
        <HelpVideosPanel
          videos={hv.videos.list}
          onPlay={hv.playVideo}
          onClose={hv.closeHelpVideos}
          returnFocusTo={() => hv.panelReturnRef.current}
        />
      )}
      {hv.activeVideo && (
        <HelpVideoPlayer
          video={hv.activeVideo}
          onClose={hv.closePlayer}
          returnFocusTo={() => hv.playerReturnRef.current}
        />
      )}
    </>
  );
}

/** onboardingTour ON: the tour hook runs; the videos side follows helpVideos. */
function EnabledOnboardingProvider({ user, isNavOpen, setIsNavOpen, appModalOpen, systemModalOpen, videosEnabled, children }) {
  const tour = useOnboardingTour({ user, isNavOpen, setIsNavOpen, appModalOpen, systemModalOpen, videosEnabled });
  return (
    <OnboardingContext.Provider value={tour.value}>
      {children}
      {videosEnabled && <HelpVideosSurface hv={tour} />}
    </OnboardingContext.Provider>
  );
}

/**
 * helpVideos ON, onboardingTour OFF (the prod posture in v3.76.0/1): only
 * the videos state mounts. The tour hook never runs — no auto-start, no
 * hints, no Take the tour, no user-doc onboarding write; the tour-side
 * context values are the inert stub's.
 */
function HelpVideosOnlyProvider({ user, children }) {
  const userRef = useRef(user);
  userRef.current = user;
  const hv = useHelpVideos({ userRef, enabled: true });
  const value = useMemo(() => ({
    ...DISABLED_ONBOARDING,
    videosEnabled: true,
    prefetchHelpVideos: hv.prefetchHelpVideos,
    openHelpVideos: hv.openHelpVideos,
    helpVideosStatus: hv.videos.status,
    hasHelpVideos: hv.videos.list.length > 0,
  }), [hv.prefetchHelpVideos, hv.openHelpVideos, hv.videos]);
  return (
    <OnboardingContext.Provider value={value}>
      {children}
      <HelpVideosSurface hv={hv} />
    </OnboardingContext.Provider>
  );
}

/**
 * Flag-gated root. `onboardingTour` decides whether the tour hook mounts;
 * `helpVideos` decides whether the videos side (read, panel, player, Help
 * items) exists. With both OFF this is a pass-through that provides the
 * inert stub: no hook runs, no read, no Help items, no CSS class ever
 * applied — the Help group renders exactly as before v3.75.0.
 */
export default function OnboardingProvider(props) {
  const tourOn = isFeatureEnabled('onboardingTour');
  const videosOn = isFeatureEnabled('helpVideos');
  if (tourOn) return <EnabledOnboardingProvider {...props} videosEnabled={videosOn} />;
  if (videosOn) return <HelpVideosOnlyProvider {...props} />;
  return <OnboardingContext.Provider value={DISABLED_ONBOARDING}>{props.children}</OnboardingContext.Provider>;
}
